import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter, 
  CreditCard, 
  AlertTriangle,
  Calendar,
  DollarSign,
  FileText,
  Plus
} from 'lucide-react';
import { useAccountsPayable } from '@/hooks/useAccountsPayable';
import { accountsPayableService } from '@/services/accountsPayableService';
import { AccountsPayableTable } from './components/AccountsPayableTable';
import { AccountsPayableStats } from './components/AccountsPayableStats';
import { PaymentModal } from './components/PaymentModal';
import { AccountPayableModal } from './components/AccountPayableModal';
import { InvoiceEntryModal } from './components/InvoiceEntryModal';
import type { AccountPayable, InvoiceFormData } from '@/types/accounts-payable.types';

export const AccountsPayablePage: React.FC = () => {
  const {
    accounts: allAccounts,
    stats,
    filters,
    loading,
    error,
    meta,
    updateFilters,
    registerPayment,
    createAccount,
    getOverdueAccounts
  } = useAccountsPayable();

  const [selectedAccount, setSelectedAccount] = useState<AccountPayable | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>(filters.status || 'all');

  const overdueAccounts = getOverdueAccounts();

  // Filter accounts based on selected status
  const accounts = React.useMemo(() => {
    if (selectedStatus === 'overdue') {
      return overdueAccounts;
    }
    if (selectedStatus === 'all') {
      return allAccounts;
    }
    return allAccounts.filter(account => account.status === selectedStatus);
  }, [allAccounts, overdueAccounts, selectedStatus]);

  // Status filter options
  const statusFilters: Array<{ key: string, label: string, color: string }> = [
    { key: 'all', label: 'Todos', color: 'bg-gray-100 text-gray-800' },
    { key: 'pending', label: 'Pendientes', color: 'bg-yellow-100 text-yellow-800' },
    { key: 'partially_paid', label: 'Pagadas Parcialmente', color: 'bg-orange-100 text-orange-800' },
    { key: 'paid', label: 'Pagadas', color: 'bg-green-100 text-green-800' },
    { key: 'overdue', label: 'Vencidas', color: 'bg-red-100 text-red-800' }
  ];

  // Get count for each status
  const getStatusCount = (status: string) => {
    if (status === 'all') return allAccounts.length;
    if (status === 'overdue') {
      return overdueAccounts.length;
    }
    return allAccounts.filter(account => account.status === status).length;
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    updateFilters({ supplier: value });
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    // Don't update filters for overdue since we handle it locally
    if (status !== 'overdue') {
      updateFilters({ status: status as any });
    }
  };


  const handlePaymentClick = (account: AccountPayable) => {
    setSelectedAccount(account);
    setShowPaymentModal(true);
  };

  const handleViewClick = (account: AccountPayable) => {
    window.open(`/accounts-payable/${account.id}`, '_blank');
  };

  const handlePaymentSubmit = async (paymentData: any) => {
    if (selectedAccount) {
      const success = await registerPayment({
        ...paymentData,
        accountPayableId: selectedAccount.id
      });
      
      if (success) {
        setShowPaymentModal(false);
        setSelectedAccount(null);
      }
    }
  };

  const handleCreateAccount = async (accountData: any) => {
    const success = await createAccount(accountData);
    if (success) {
      setShowAccountModal(false);
    }
  };

  const handleInvoiceEntryClick = (account: AccountPayable) => {
    setSelectedAccount(account);
    setShowInvoiceModal(true);
  };

  const handleInvoiceSubmit = async (accountId: string, invoiceData: InvoiceFormData) => {
    try {
      // TODO: Implement invoice entry service call
      console.log('Invoice submission:', { accountId, invoiceData });
      
      // For now, simulate success and close modal
      setShowInvoiceModal(false);
      setSelectedAccount(null);
      
      // TODO: Refresh accounts data to show updated invoice status
    } catch (error) {
      console.error('Error submitting invoice:', error);
      throw error;
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <span>Error al cargar cuentas por pagar: {error}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cuentas por Pagar</h1>
          <p className="text-gray-600">
            Gestión de obligaciones financieras con proveedores
          </p>
        </div>
        
        <Button onClick={() => setShowAccountModal(true)} className="w-fit">
          <Plus className="mr-2 h-4 w-4" />
          Nueva Cuenta
        </Button>
      </div>


      {/* Estadísticas */}
      <AccountsPayableStats stats={stats} loading={loading} />

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filtros de Búsqueda */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Filtro por proveedor */}
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Proveedor
              </label>
              <Select value={filters.supplier || 'all'} onValueChange={(value) => handleSearch(value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proveedores</SelectItem>
                  <SelectItem value="Proveedor ABC S.A.C.">Proveedor ABC S.A.C.</SelectItem>
                  <SelectItem value="Distribuidora XYZ E.I.R.L.">Distribuidora XYZ E.I.R.L.</SelectItem>
                  <SelectItem value="Suministros DEF S.A.">Suministros DEF S.A.</SelectItem>
                  <SelectItem value="Comercial GHI Ltda.">Comercial GHI Ltda.</SelectItem>
                  <SelectItem value="Importadora JKL S.R.L.">Importadora JKL S.R.L.</SelectItem>
                  <SelectItem value="Alimentos MNO S.A.C.">Alimentos MNO S.A.C.</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por fecha desde */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Fecha Desde
              </label>
              <Input
                type="date"
                value={filters.dueDateFrom}
                onChange={(e) => updateFilters({ dueDateFrom: e.target.value })}
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Fecha Hasta
              </label>
              <Input
                type="date"
                value={filters.dueDateTo}
                onChange={(e) => updateFilters({ dueDateTo: e.target.value })}
              />
            </div>

            {/* Botón de búsqueda */}
            <div>
              <Button size="sm" className="px-3">
                <Search className="mr-2 h-4 w-4" />
                Buscar
              </Button>
            </div>
          </div>

          {/* Separador */}
          <div className="border-t border-gray-200"></div>

          {/* Filtros de Estado */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Estado</h4>
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((filter) => {
                const count = getStatusCount(filter.key);
                const isActive = selectedStatus === filter.key;
                
                return (
                  <Button
                    key={filter.key}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleStatusFilter(filter.key)}
                    className={`${
                      isActive 
                        ? 'bg-macartur-primary hover:bg-macartur-primary/90' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`inline-flex items-center gap-1 ${
                      isActive ? 'text-white' : ''
                    }`}>
                      {filter.label}
                      <Badge 
                        variant="secondary" 
                        className={`ml-1 text-xs ${
                          isActive 
                            ? 'bg-white/20 text-white border-white/30' 
                            : filter.color
                        }`}
                      >
                        {count}
                      </Badge>
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Filtros activos */}
          {(filters.supplier || filters.dueDateFrom || filters.dueDateTo) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {filters.supplier && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => handleSearch('')}>
                  Proveedor: {filters.supplier} ✕
                </Badge>
              )}
              {filters.dueDateFrom && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilters({ dueDateFrom: '' })}>
                  Desde: {accountsPayableService.formatDate(filters.dueDateFrom)} ✕
                </Badge>
              )}
              {filters.dueDateTo && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilters({ dueDateTo: '' })}>
                  Hasta: {accountsPayableService.formatDate(filters.dueDateTo)} ✕
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de cuentas por pagar */}
      <AccountsPayableTable
        accounts={accounts}
        loading={loading}
        onPaymentClick={handlePaymentClick}
        onViewClick={handleViewClick}
        onInvoiceEntryClick={handleInvoiceEntryClick}
      />

      {/* Modal de registro de pago */}
      {showPaymentModal && selectedAccount && (
        <PaymentModal
          account={selectedAccount}
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedAccount(null);
          }}
          onSubmit={handlePaymentSubmit}
        />
      )}

      {/* Modal de nueva cuenta por pagar */}
      <AccountPayableModal
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        onSubmit={handleCreateAccount}
      />

      {/* Modal de ingreso de factura */}
      <InvoiceEntryModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        account={selectedAccount}
        onSubmit={handleInvoiceSubmit}
      />

      {/* Resumen de resultados */}
      {meta.total > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600">
              Mostrando {accounts.length} de {meta.total} cuentas por pagar
              {meta.hasMore && (
                <span className="ml-2 text-blue-600">
                  (hay más resultados disponibles)
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};